import type { EatingProfile, EatingStyle, EatingScale } from '@/lib/eatingProfile';
import { NORMS } from '@/lib/eatingProfile';

// ════════════════════════════════════════════════════════════════
// «Старт с Дофи» (30.10.2026, план docs/2026-10-30-dofi-start.md).
//
// Решение Славы: ядро продукта — Дофи, а не счётчик. Новичок проходит тест,
// получает «портрет» (узнаёт себя), и Дофи сразу ведёт его три дня —
// по одному короткому заданию в день. Три дня совпадают с пробным периодом,
// в конце — разбор, где часть выводов открыта, остальное — с подпиской.
//
// Портрет собирается из ОТВЕТОВ человека, а не из общих слов: это то,
// что отличает его от гороскопа. Правила (Слава как психолог):
// — только пищевое поведение и привычки, не характер;
// — «похоже», а не «ты такой»;
// — никаких фактов вне ответов и дневника;
// — сила выраженности честно: мягкий тип описывается как мягкий;
// — подпись «это не диагноз» остаётся всегда.
// Тексты — черновики Claude, проверяет Слава. Править здесь.
// ════════════════════════════════════════════════════════════════

type Sex = 'male' | 'female' | null | undefined;
const g = (sex: Sex, m: string, f: string) => (sex === 'female' ? f : m);

// Ситуации из полного DEBQ — по номеру вопроса (0..32), как в FULL_QUESTIONS
const FULL_SITUATIONS: Record<number, string> = {
  // ограничительное
  0: 'вес начинает расти — и ты ешь меньше', 1: 'ешь меньше, чем хочется',
  2: 'отказываешься от еды из-за веса', 3: 'аккуратно контролируешь, сколько съел',
  4: 'выбираешь еду, чтобы похудеть', 5: 'после переедания на следующий день урезаешь еду',
  6: 'ешь меньше, чтобы не поправиться', 7: 'не ешь между приёмами из-за веса',
  8: 'не ешь вечером из-за веса', 9: 'думаешь о весе, когда ешь',
  // эмоциональное
  10: 'раздражён', 11: 'нечего делать', 12: 'подавлен', 13: 'одиноко', 14: 'кто-то подвёл',
  15: 'что-то не получается', 16: 'предчувствуешь неприятность', 17: 'напряжён или встревожен',
  18: '«всё валится из рук»', 19: 'испуган', 20: 'разочарован', 21: 'расстроен', 22: 'скучно или устал',
  // внешнее
  23: 'еда вкусная', 24: 'еда красиво выглядит и пахнет', 25: 'видишь вкусное и чувствуешь запах',
  26: 'вкусное под рукой', 27: 'проходишь мимо кондитерской', 28: 'проходишь мимо кафе',
  29: 'видишь, как едят другие', 31: 'ешь в компании', 32: 'пробуешь, пока готовишь',
};
const FULL_RANGE: Record<EatingScale, [number, number]> = { restrained: [0, 9], emotional: [10, 22], external: [23, 32] };

// Короткий скрининг из онбординга (6 вопросов)
const SHORT_SITUATIONS: Record<number, string> = {
  0: 'тревожно, грустно или злишься', 1: 'день был тяжёлым',
  2: 'видишь вкусное или чувствуешь запах', 3: 'ешь в компании или еда рядом',
  4: 'ешь меньше, чем хочется', 5: 'запрещаешь себе продукты, а потом срываешься',
};
const SHORT_RANGE: Record<EatingScale, [number, number]> = { emotional: [0, 1], external: [2, 3], restrained: [4, 5] };

/** Ситуации ведущей шкалы, которые человек отметил «иногда» и чаще — самые частые первыми. */
export function situations(ep: EatingProfile, scale: EatingScale, max = 4): string[] {
  const full = ep.version === 2;
  const [a, b] = (full ? FULL_RANGE : SHORT_RANGE)[scale];
  const map = full ? FULL_SITUATIONS : SHORT_SITUATIONS;
  const picked: { i: number; v: number }[] = [];
  for (let i = a; i <= b; i++) {
    const v = Number(ep.answers?.[i]) || 0;
    if (v >= 3 && map[i]) picked.push({ i, v });
  }
  picked.sort((x, y) => y.v - x.v || x.i - y.i);
  return picked.slice(0, max).map((p) => map[p.i]);
}

function listRu(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} и ${items[items.length - 1]}`;
}

export type Portrait = { title: string; intensity: string | null; paragraphs: string[] };

export function buildPortrait(ep: EatingProfile, sex: Sex): Portrait {
  const style = ep.style;
  const marked = g(sex, 'отметил', 'отметила');
  const excess = style === 'balanced' ? 0 : Math.round((ep[style] - NORMS[style]) * 10) / 10;
  const intensity = style === 'balanced' ? null
    : excess < 1 ? 'Мягкая форма: чуть выше обычного, не ярко выраженная.'
    : 'Выражено заметно — с этим стоит поработать в первую очередь.';
  const sit = style === 'balanced' ? [] : situations(ep, style);
  const P: string[] = [];

  if (style === 'emotional') {
    P.push('Похоже, еда для тебя — не столько про голод, сколько про то, чтобы переключиться.');
    if (sit.length) {
      P.push(`Ты ${marked}, что желание поесть чаще появляется, когда ${listRu(sit)}. ` +
        'Снаружи это разные состояния, но устроены они одинаково: рука тянется к еде раньше, чем ты успеваешь заметить, что дело не в желудке.');
    }
    const canStop = ep.version === 2 && (Number(ep.answers?.[30]) || 0) >= 4;
    const fewRules = ep.restrained < NORMS.restrained;
    if (canStop || fewRules) {
      P.push('При этом ' + [
        canStop ? `ты не из тех, кто не может остановиться: ты ${marked}, что обычно останавливаешься, даже когда очень вкусно` : '',
        fewRules ? 'строгих запретов на еду у тебя нет — ты скорее договариваешься с собой, чем воюешь' : '',
      ].filter(Boolean).join('. И ') + '.');
    }
    P.push('Поэтому тебе не нужен контроль жёстче. Тебе нужно научиться замечать момент между «напрягся» и «уже жую».');
  } else if (style === 'external') {
    P.push('Похоже, тебя ведёт не голод, а то, что вокруг: запах из пекарни, тарелка на столе, кто-то рядом ест.');
    if (sit.length) {
      P.push(`Ты ${marked}, что ешь больше, когда ${listRu(sit)}. Знакомо: не хотелось есть, пока не увидел${g(sex, '', 'а')}. А увидел${g(sex, '', 'а')} — и уже хочется, причём прямо сейчас.`);
    }
    P.push('Это не обжорство и не слабость. Так работает внимание: мозг замечает еду раньше, чем ты успеваешь спросить себя, голоден ли.');
    P.push('Поэтому запреты тут почти не работают — соблазнов вокруг слишком много. Работает другое: поменять то, что видишь, и дать себе паузу между «увидел» и «съел».');
  } else if (style === 'restrained') {
    P.push('Похоже, ты много контролируешь: сколько съел, что можно, что нельзя. И часто держишься дольше, чем хотелось бы.');
    if (sit.length) {
      P.push(`Ты ${marked}, что ${listRu(sit)}. Снаружи это выглядит как дисциплина. А изнутри ощущается как постоянная борьба, которая однажды заканчивается срывом — и всё по кругу.`);
    }
    P.push('Это не отсутствие силы воли. Чем жёстче запрет, тем сильнее тянет к запретному — так устроены мы все.');
    P.push('Поэтому тебе нужно не больше контроля, а меньше войны: есть достаточно, не наказывать себя за срыв и перестать делить еду на «можно» и «нельзя».');
  } else {
    P.push('Похоже, с едой у тебя в целом спокойные отношения: ты не заедаешь чувства, не ведёшься на каждую витрину и не держишь себя в жёстких рамках.');
    P.push('Значит, если вес не идёт, дело скорее не в голове, а в мелочах, которые легко не заметить: перекусы на бегу, «чуть-чуть» за ужином, ритм дня, когда к вечеру съедается больше, чем кажется.');
    P.push('Это хорошая новость: такие вещи находятся за несколько дней дневника и меняются без борьбы с собой.');
  }
  return { title: TITLES[style], intensity, paragraphs: P };
}

const TITLES: Record<EatingStyle, string> = {
  emotional: 'Твой тип — эмоциональное пищевое поведение',
  external: 'Твой тип — внешнее пищевое поведение',
  restrained: 'Твой тип — ограничительное пищевое поведение',
  balanced: 'У тебя нет ярко выраженного стиля',
};

export const START_BRIDGE: Record<EatingStyle, string> = {
  emotional: 'С этим лучше всего работают небольшие ритуалы осознанности — по минуте в день, без запретов и подсчётов. Я помогу начать: три дня, по одному короткому заданию в день.',
  external: 'С этим лучше всего работают небольшие ритуалы осознанности — по минуте в день. Я помогу начать: три дня, по одному короткому заданию в день.',
  restrained: 'Я помогу начать — мягко и без новых запретов. Три дня, по одному короткому заданию в день.',
  balanced: 'Я помогу их найти. Три дня, по одному короткому заданию в день.',
};

// ── Задания ─────────────────────────────────────────────────────

export type TaskKind = 'mark' | 'choice' | 'lunch' | 'log';
export type StartTask = {
  title: string;
  text: string;
  kind: TaskKind;
  /** Для «choice»: кнопки-счётчики, по тапу +1 */
  choices?: { key: string; label: string }[];
  /** Кнопка перехода (практика, дневник) */
  action?: { label: string; to: string };
};

export const START_TASKS: Record<EatingStyle, [StartTask, StartTask, StartTask]> = {
  emotional: [
    { title: 'Стоп-кадр', kind: 'choice',
      text: 'Перед едой вне обычного приёма — пауза 30 секунд и один вопрос: это голод или напряжение? Ответ — кнопкой, каждый раз.',
      choices: [{ key: 'hunger', label: 'Голод' }, { key: 'tension', label: 'Напряжение' }] },
    { title: 'Обед до 15:00', kind: 'lunch',
      text: 'Нормальный обед с белком до трёх часов дня. Больше ничего не меняем — посмотрим, что будет с вечером.' },
    { title: 'Выдох вместо перекуса', kind: 'mark',
      text: 'Когда напрягся и тянет к еде — сначала 2 минуты дыхания. Потом реши, хочется ли ещё.',
      action: { label: 'Открыть дыхание', to: '/breathing/physiological_sigh' } },
  ],
  external: [
    { title: 'Убрать с глаз', kind: 'mark',
      text: 'Спрячь одну вкусность из поля зрения: со стола — в шкаф, в непрозрачный контейнер. На видное место — фрукты.' },
    { title: 'Пауза 10 секунд', kind: 'choice',
      text: 'Увидел вкусное — 10 секунд и вопрос: я голоден или просто увидел? Ответ — кнопкой, каждый раз.',
      choices: [{ key: 'hunger', label: 'Голоден' }, { key: 'saw', label: 'Просто увидел' }] },
    { title: 'Тарелка, а не упаковка', kind: 'mark',
      text: 'Любой перекус — в тарелку, есть сидя и без телефона. Так проще заметить, что уже сыт.' },
  ],
  restrained: [
    { title: 'Без вины', kind: 'choice',
      text: 'Съешь одну любимую еду, которую обычно себе запрещаешь, — в обычной порции и спокойно. Как это было?',
      choices: [{ key: 'calm', label: 'Спокойно' }, { key: 'guilt', label: 'Было неловко' }] },
    { title: 'Сытый обед', kind: 'lunch',
      text: 'Обед, после которого не хочется есть ещё часа три. Не «поменьше», а достаточно.' },
    { title: 'Отмена наказания', kind: 'mark',
      text: 'Если вчера съел больше, чем хотел, — сегодня ешь как обычно, без урезания. Это и есть задание.' },
  ],
  balanced: [
    { title: 'Честный день', kind: 'log',
      text: 'Запиши в дневник всё за день, включая перекусы и «кусочек». Не чтобы оценить — чтобы увидеть.',
      action: { label: 'Записать еду', to: '/add' } },
    { title: 'Обед до 15:00', kind: 'lunch',
      text: 'Нормальный обед с белком до трёх часов дня. Посмотрим, что изменится к вечеру.' },
    { title: 'Вечер без перекуса', kind: 'mark',
      text: 'После ужина — чай, прогулка или 2 минуты дыхания вместо холодильника.',
      action: { label: 'Открыть дыхание', to: '/breathing/physiological_sigh' } },
  ],
};

// ── Отметки и день программы ───────────────────────────────────

export type MarkStatus = 'done' | 'partial' | 'missed';
export type StartMark = { day: 1 | 2 | 3; status: MarkStatus; choice: string | null; note: string | null };

/** «hunger=2;tension=3» ⇄ {hunger:2, tension:3} */
export function parseChoice(s: string | null | undefined): Record<string, number> {
  const out: Record<string, number> = {};
  for (const part of String(s || '').split(';')) {
    const [k, v] = part.split('=');
    if (k && Number(v) > 0) out[k] = Number(v);
  }
  return out;
}
export function formatChoice(c: Record<string, number>): string | null {
  const s = Object.entries(c).filter(([, v]) => v > 0).map(([k, v]) => `${k}=${v}`).join(';');
  return s || null;
}

function localDayIndex(d: Date): number {
  return Math.floor((d.getTime() - d.getTimezoneOffset() * 60000) / 86400000);
}
/** День программы: 1..3, 4 — программа завершена. Считается по календарным дням человека. */
export function startDay(startAt: string | null | undefined, now = new Date()): number {
  if (!startAt) return 0;
  return Math.min(4, localDayIndex(now) - localDayIndex(new Date(startAt)) + 1);
}

// ── Разбор трёх дней ───────────────────────────────────────────

export type MealLite = { consumed_at: string; total_calories: number | null };
export type ReviewLine = { emoji: string; text: string };

const hourOf = (iso: string) => new Date(iso).getHours();

function rhythm(meals: MealLite[], fromIdx: number, toIdx: number) {
  const days = new Map<number, { before15: number; after19: number; late: number; total: number }>();
  for (const m of meals) {
    const d = new Date(m.consumed_at);
    const di = localDayIndex(d);
    if (di < fromIdx || di > toIdx) continue;
    const kcal = Number(m.total_calories) || 0;
    const r = days.get(di) ?? { before15: 0, after19: 0, late: 0, total: 0 };
    const h = hourOf(m.consumed_at);
    if (h < 15) r.before15 += kcal;
    if (h >= 19) r.after19 += kcal;
    if (h >= 21) r.late += 1;
    r.total += kcal;
    days.set(di, r);
  }
  const vals = [...days.values()].filter((r) => r.total > 0);
  const avg = (k: 'before15' | 'after19' | 'late' | 'total') => vals.length ? vals.reduce((a, r) => a + r[k], 0) / vals.length : 0;
  return { n: vals.length, before15: avg('before15'), after19: avg('after19'), late: avg('late'), total: avg('total') };
}

const CHOICE_WORD: Record<string, string> = { hunger: 'голод', tension: 'напряжение', saw: '«просто увидел»', calm: 'спокойно', guilt: 'неловко' };

/** Выводы разбора, по силе. Считает код, не модель. Первые два показываются всем. */
export function buildReview(style: EatingStyle, startAt: string, marks: StartMark[], meals: MealLite[]): ReviewLine[] {
  const lines: ReviewLine[] = [];
  const start = localDayIndex(new Date(startAt));
  const done = marks.filter((m) => m.status === 'done').length;
  const partial = marks.filter((m) => m.status === 'partial').length;
  const choices: Record<string, number> = {};
  for (const m of marks) for (const [k, v] of Object.entries(parseChoice(m.choice))) choices[k] = (choices[k] || 0) + v;
  const totalChoice = Object.values(choices).reduce((a, b) => a + b, 0);

  if (totalChoice > 0) {
    const notHunger = totalChoice - (choices.hunger || 0);
    if (style === 'emotional' || style === 'external') {
      const word = style === 'emotional' ? 'напряжение' : '«просто увидел»';
      lines.push({ emoji: '⏸', text: `Пауза сработала ${totalChoice} раз. В ${notHunger} из ${totalChoice} это было ${word}, а не голод.${notHunger > 0 ? ' Значит, часть «хочу есть» — совсем не про еду.' : ''}` });
    } else {
      const parts = Object.entries(choices).map(([k, v]) => `${CHOICE_WORD[k] ?? k} — ${v}`).join(', ');
      lines.push({ emoji: '🍫', text: `Любимая еда без вины: ${parts}. Первый шаг к тому, чтобы запрет перестал управлять тобой.` });
    }
  }
  lines.push({ emoji: '✅', text: `Заданий выполнено: ${done} из 3${partial ? `, ещё ${partial} частично` : ''}. ${done + partial >= 2 ? 'Это уже не случайность, а начало привычки.' : 'Даже одна попытка показывает, где у тебя точка опоры.'}` });

  const before = rhythm(meals, start - 7, start - 1);
  const during = rhythm(meals, start, start + 2);
  if (during.n >= 2 && before.n >= 2) {
    const d15 = Math.round(during.before15 - before.before15);
    if (Math.abs(d15) >= 100) {
      lines.push({ emoji: '🕒', text: `До 15:00 ты теперь ешь в среднем ${Math.round(during.before15)} ккал, а до старта было ${Math.round(before.before15)}. ${d15 > 0 ? 'День стал сытнее — вечеру меньше нечего «добирать».' : 'Днём еды стало меньше — посмотрим, не отыгрывается ли вечер.'}` });
    }
    const d19 = Math.round(during.after19 - before.after19);
    if (Math.abs(d19) >= 100) {
      lines.push({ emoji: '🌙', text: `После 19:00 — ${Math.round(during.after19)} ккал в среднем против ${Math.round(before.after19)} до старта. ${d19 < 0 ? 'Вечер стал спокойнее.' : 'Вечер пока берёт своё — это и будет следующим шагом.'}` });
    }
    if (before.late >= 0.5 || during.late >= 0.5) {
      lines.push({ emoji: '🕘', text: `Приёмов после 21:00: ${during.late.toFixed(1).replace('.', ',')} в день против ${before.late.toFixed(1).replace('.', ',')} до старта.` });
    }
  } else if (during.n >= 1) {
    lines.push({ emoji: '🕒', text: `За эти дни до 15:00 в среднем ${Math.round(during.before15)} ккал, после 19:00 — ${Math.round(during.after19)}. Через неделю дневника я покажу, как это меняется.` });
  }
  lines.push({ emoji: '🧭', text: style === 'emotional'
    ? 'Следующий шаг — 7 дней «вечера без заедания»: научимся снимать напряжение раньше, чем оно дойдёт до холодильника.'
    : style === 'external'
      ? 'Следующий шаг — 7 дней «среда за тебя»: настроим дом и день так, чтобы соблазнов стало меньше без силы воли.'
      : style === 'restrained'
        ? 'Следующий шаг — 7 дней «еда без войны»: уберём ещё пару запретов и посмотрим, как уходят срывы.'
        : `Следующий шаг — 7 дней дневника: найдём, где лишнее набирается незаметно.` });
  return lines;
}

/** Сколько выводов разбора открыто без подписки */
export const REVIEW_FREE = 2;
